// Lochreihe im System-32-Raster — der Schreiner-Standard für Fachbodenträger
// und Beschläge. Erzeugt fertige Bohrungen im Format von cutouts.js
// (shape "circle", Sackloch über depth_mm, 0 = durchgehend). Engine-frei.
//
// Bezug wie in der Werkstatt gemessen, also ab Plattenkante zur Bohrungsmitte:
//   direction "length": Reihe läuft entlang der Länge (x), startOffset ab der
//     linken Kante, edgeOffset ist der Abstand zur vorderen Längskante (z).
//   direction "width": Reihe läuft entlang der Breite (z), startOffset ab der
//     vorderen Kante, edgeOffset ist der Abstand zur linken Kante (x).
//
// Klemmen statt Rechnen: jede Bohrung läuft durch clampCutout aus cutouts.js.
// Die erste Bohrung darf dabei nach innen rutschen (zu kleiner Randabstand),
// alle weiteren müssen exakt im Raster liegen — passt eine nicht mehr auf die
// Platte, endet die Reihe dort. So bleibt das 32er-Raster garantiert sauber
// und es stapeln sich keine geklemmten Bohrungen auf derselben Stelle.
//
// Zusammenhalt: alle Bohrungen einer Reihe tragen source "hole_row" und
// dieselbe row_id (nicht-geometrische Zusatzfelder, die das Klemmen überleben —
// siehe cutouts.js). Damit bleibt die Reihe als Gruppe erkennbar und lässt sich
// in einem Schritt entfernen, obwohl sie als lose Einzelbohrungen gespeichert
// ist. Altbestände ohne diese Felder bleiben unverändert Einzelbohrungen.
import { clampCutout, toEdgeDistance, fromEdgeDistance, cutoutsOf } from "./cutouts"
import { generateClientId } from "./document"

export const HOLE_ROW_DEFAULTS = {
  count: 10,
  pitch: 32,
  diameter: 5,
  depth: 13,
  startOffset: 37,
  edgeOffset: 37,
  direction: "length"
}

export const HOLE_ROW_MIN_COUNT = 2
export const HOLE_ROW_MAX_COUNT = 60

// Herkunfts-Kennung im cutout (vgl. "joinery" im Verbindungs-Assistenten)
export const HOLE_ROW_SOURCE = "hole_row"

// Rundungsspielraum: clampCutout rundet die Mittelpunkt-Koordinate auf ganze
// mm, der Kantenabstand kann dadurch um bis zu ½ mm abweichen.
const TOLERANCE = 0.5 + 1e-9

// rowId ist nur für Tests/Wiederholungen gedacht — normal entsteht je Aufruf
// eine frische Id (Muster generateClientId aus document.js).
export function holeRowCutouts({ count, pitch, diameter, depth, startOffset, edgeOffset, direction, part, rowId } = {}) {
  if (!part) return []
  const rowAxis = direction === "width" ? "z" : "x"
  const crossAxis = rowAxis === "x" ? "z" : "x"
  const total = clampInt(count, HOLE_ROW_MIN_COUNT, HOLE_ROW_MAX_COUNT, HOLE_ROW_DEFAULTS.count)
  const step = positive(pitch, HOLE_ROW_DEFAULTS.pitch)
  const spec = {
    part,
    rowAxis,
    crossAxis,
    diameter: positive(diameter, HOLE_ROW_DEFAULTS.diameter),
    depth: Number.isFinite(depth) && depth > 0 ? depth : 0,
    cross: Number.isFinite(edgeOffset) ? edgeOffset : HOLE_ROW_DEFAULTS.edgeOffset,
    rowId: rowId || generateClientId()
  }

  // Startbohrung setzen und ihren tatsächlichen (ggf. geklemmten) Kantenabstand
  // als Nullpunkt des Rasters nehmen — sonst verzöge ein zu kleiner Randabstand
  // nur den ersten Abstand.
  const first = boreAt(Number.isFinite(startOffset) ? startOffset : HOLE_ROW_DEFAULTS.startOffset, spec)
  const bores = [ first ]
  const start = toEdgeDistance(first, part, rowAxis)
  for (let k = 1; k < total; k++) {
    const wanted = start + k * step
    const bore = boreAt(wanted, spec)
    // Geklemmt = Platte zu Ende: Reihe hier kürzen statt Bohrungen zu stapeln
    if (Math.abs(toEdgeDistance(bore, part, rowAxis) - wanted) > TOLERANCE) break
    bores.push(bore)
  }
  return bores
}

// Eine einzelne Bohrung am gewünschten Kantenabstand — Lage über
// fromEdgeDistance, Grenzen über clampCutout (beides aus cutouts.js)
function boreAt(rowDistance, { part, rowAxis, crossAxis, diameter, depth, cross, rowId }) {
  const bore = {
    shape: "circle", diameter_mm: diameter, x_mm: 0, z_mm: 0,
    source: HOLE_ROW_SOURCE, row_id: rowId
  }
  // depth 0 (bzw. ≥ Stärke) verwirft clampCutout → durchgehende Bohrung
  if (depth > 0) {
    bore.depth_mm = depth
    bore.side = "top"
  }
  bore[`${rowAxis}_mm`] = fromEdgeDistance(rowDistance, bore, part, rowAxis)
  bore[`${crossAxis}_mm`] = fromEdgeDistance(cross, bore, part, crossAxis)
  return clampCutout(bore, part)
}

// --- Reihen als Gruppe ------------------------------------------------------

// Reihen-Id einer Bohrung, sonst null (Altbestand ohne source/row_id ebenso).
export function holeRowIdOf(cutout) {
  if (cutout?.source !== HOLE_ROW_SOURCE) return null
  return typeof cutout.row_id === "string" && cutout.row_id ? cutout.row_id : null
}

// Alle Reihen eines Teils, in der Reihenfolge ihres ersten Auftretens.
export function holeRowIds(part) {
  const ids = []
  for (const cutout of cutoutsOf(part)) {
    const id = holeRowIdOf(cutout)
    if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}

// Anzahl der (noch) vorhandenen Bohrungen einer Reihe — einzeln gelöschte
// zählen nicht mehr mit, der Rest bleibt eine Reihe.
export function holeRowSize(part, rowId) {
  if (!rowId) return 0
  return cutoutsOf(part).filter((cutout) => holeRowIdOf(cutout) === rowId).length
}

// Ausschnitte des Teils ohne die Bohrungen dieser Reihe — Grundlage fürs
// Entfernen der ganzen Reihe in einem Undo-Schritt.
export function withoutHoleRow(part, rowId) {
  return cutoutsOf(part).filter((cutout) => !rowId || holeRowIdOf(cutout) !== rowId)
}

function clampInt(value, min, max, fallback) {
  const number = Number.isFinite(value) ? Math.round(value) : fallback
  return Math.min(Math.max(number, min), max)
}

function positive(value, fallback) {
  return Number.isFinite(value) && value > 0 ? value : fallback
}
